import React, { useState } from "react";
import moment from "moment";
import { API_URL } from "src/scenes/App";
import axios from "axios";
import styled from "styled-components";
import { UpperControls,
    ActivateFeatureButton,
    Button,
    DataTable,
    DataTableCell,
    DataTableContainer,
    DataTableEditActionButton,
    DataTableHeaderCheckboxContent,
    DataTableHeaderCell,
    DataTableLoading,
    DataTableViewActionButton,
    PortalPageShell,
    ConfirmModal,
    Toast,
    PortalSelect,
    PortalTableActions,
    PortalTableControls
} from "src/components";
import AspectRatioImageThumbnail from "src/components/AspectRatioImageThumbnail";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
//assets
import ArrowDown from "src/img/employeeList/SortUpArrow.svg";
import ArrowUp from "src/img/employeeList/SortDownArrow.svg";


import Checkbox from "./Checkbox";
import { SurveyStatusType, SortableColumnState, SortableColumns } from "src/constants/survey";
import SurveyAndPollsHeading from "./SurveyAndPollsHeading";
import { TableRowCell } from "src/components/TableRowCell";
import { EnumUserGroup, surveyTypeOptions } from "./Enums";

const SurveyButton = styled(Button).attrs(p => ({
    padding: p.padding || "10px 40px 10px",
    borderRadius: p.borderRadius || "8px !important",
    color: p.color || "#FFFFFF",
    cursor: p.cursor || "pointer"
}))`
    padding: 10px 40px 10px;
    border-radius: 8px !important;
    color: #FFFFFF;
    position: relative;
    cursor: pointer;
    margin: 0 1rem;

    @media (max-width: 768px) {
       margin: 0 0rem;
    }
`;

const ColumnHeader = (({ heading, sortDirection, styleProps }) => {
    const Container = styled.div`
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: center;

        text-align: center;
        user-select: none;
        cursor: pointer;
    `;

    const SortIndicator = styled.div`
        display: flex;
        flex-direction: column;
        justify-content: center;

        padding-left: 8px;
    `;

    return (
        <Container style={{ ...styleProps }}>
            {heading}
            <SortIndicator hidden={sortDirection === SortableColumnState.INACTIVE}>
                {(sortDirection === SortableColumnState.DESCENDING) &&
                    <img width="10px" src={ArrowDown} alt="SortArrow" />}
                {(sortDirection === SortableColumnState.ASCENDING) &&
                    <img width="10px" src={ArrowUp} alt="SortArrow" />}
            </SortIndicator>
        </Container>
    );
});

const InputSelector = styled(PortalSelect)``;

const FilterControl = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    color: var(--calendar-text, #171A22);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 800;

    @media (max-width: 760px) {
        align-items: stretch;
        flex-direction: column;
        width: 100%;
    }
`;

const SurveyTypeIconGroup = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;

    img {
        height: 2.5rem;
        width: 2.5rem;
        object-fit: contain;
        vertical-align: middle;
    }
`;

const getSurveyUserGroupLabel = (survey) => {
    const employeeLabel = capitalizeString(pluralize(customNames.employees));
    const clientLabel = capitalizeString(pluralize(customNames.clients));

    if (survey.userGroup === EnumUserGroup.EMPLOYEES) {
        return employeeLabel;
    }

    if (survey.userGroup === EnumUserGroup.CLIENTS) {
        return clientLabel;
    }

    if (survey.userGroup === EnumUserGroup.ALL_USERS) {
        return `${employeeLabel} & ${clientLabel}`;
    }

    if (survey.userGroup === EnumUserGroup.SELECTED_USERS) {
        const hasSelectedEmployees = Number(survey.employeeCount) > 0;
        const hasSelectedClients = Number(survey.clientCount) > 0;

        if (hasSelectedEmployees && hasSelectedClients) {
            return `${employeeLabel} & ${clientLabel}`;
        }

        if (hasSelectedEmployees) {
            return employeeLabel;
        }

        if (hasSelectedClients) {
            return clientLabel;
        }
    }

    return survey.userGroup || "";
};

const getSurveyTypeIcons = (surveyType) => {
    const surveyOption = surveyTypeOptions.find(
        option => option.value === surveyType
    );

    if (!surveyOption) return null;

    const icons = surveyOption.icons || [surveyOption.icon];

    return (
        <SurveyTypeIconGroup title={surveyType}>
            {icons.filter(Boolean).map((icon, index) => (
                <img
                    key={`${surveyType}-${index}`}
                    src={icon}
                    alt={surveyType}
                />
            ))}
        </SurveyTypeIconGroup>
    );
};

const SurveyTable = (props) => {
    const { sortColumn, showSurveyForm, editSurvey, surveyInsights, fetchSurvey, fetching, user, setFetchingStatus, setSurveyStatusType, selectedSurveyOrPollStatusType, handleChangeTableType, selectedTableType, sortableColumns, canManageSurveys, featureContext } = props;
    const [selectedSurveys, setSelectedSurveys] = useState([]);
    const surveysArray = props.surveyArray;

    /**
     * @param {number} length
     */

    const emitSortColumn = (string) => {
        string = string.replace(/ /g, "_").toUpperCase();
        return sortColumn(string);
    };

    const handleCheckbox = () => {
        const isAllSurveysNotSelected = selectedSurveys.length !== surveysArray.length;
        if (isAllSurveysNotSelected) {
            const arr = surveysArray.map(survey => survey.uuid);
            setSelectedSurveys(arr);
        } else {
            setSelectedSurveys([]);
        }
    };

    const handleChangeStatusFilter = (surveyTypeStatus) => {
        setSurveyStatusType(surveyTypeStatus);
        fetchSurvey(surveyTypeStatus);
    };

    const addRemoveSurveyFromSelected = (survey) => {
        const isAlreadySelected = selectedSurveys.some((selectedSurvey) => selectedSurvey === survey.uuid);
        if (isAlreadySelected) {
            return setSelectedSurveys(selectedSurveys.filter((surveyUuid) => surveyUuid !== survey.uuid));
        } else {
            return setSelectedSurveys([...selectedSurveys, survey.uuid]);
        }
    };

    const checkIfUuidIsSelected = (uuid) => selectedSurveys.some(surveyUuid => surveyUuid === uuid);

    const confirmDelete = (surveyUuids) => {
        return ConfirmModal.open(
            "Permanently Remove Survey",
            <div>
                <p>Are you sure you want to remove this survey? All data from survey
                    insights for this survey will be lost</p><br></br>
                <hr/>
            </div>,
            () => {deleteSurveys(surveyUuids); },
            "Remove",
            false
        );
    };

    const deleteSurveys = (surveyUuids) => {
        ConfirmModal.close();
        setFetchingStatus(true);
        axios.delete(`${API_URL}/survey/company/deleteSurveys/`, {
            headers: {
                Authorization: "Bearer " + user.token
            },
            data: { surveyUuids: surveyUuids }
        }).then(res => {
            Toast.info("Delete successful");
            fetchSurvey(selectedSurveyOrPollStatusType);
        }).catch(error => {
            return Toast.error(error.getApiErrorDetails().message);
        }).finally(res => {
            setSelectedSurveys([]);
        });
    };

    //generate empty lines when survey/client list is less than 10 records
    const emptyList = () => {
        const emptyRows = 10 - surveysArray.length;

        if (emptyRows < 1)
            return;

        return Array.from({ length: emptyRows }, (_, i) => (
            <tr key={"empty_" + i} className="placeholder-row">
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
            </tr>
        ));
    };

    const surveyList = (surveyItems) => surveyItems.map(survey => {
        return (
            <tr key={survey.uuid}
                className={checkIfUuidIsSelected(survey.uuid) ? "selected" : ""}
            >
                <DataTableCell textAlign="center">
                    <label>
                        <Checkbox
                            readOnly
                            checked={checkIfUuidIsSelected(survey.uuid)}
                            value={survey.uuid}
                            onChange={() => addRemoveSurveyFromSelected(survey)}
                        />
                    </label>
                </DataTableCell>
                <DataTableCell width={"7%"} textAlign="center" padding="0.35rem 0.6rem">
                    {survey.image?.imageUrl && (
                        <AspectRatioImageThumbnail
                            src={survey?.image?.imageUrl}
                            alt={survey.name}
                            height="47px"
                            aspectRatio="83 / 47"
                            borderRadius="8px"
                            boxShadow="none"
                        />
                    )}
                </DataTableCell>
                <DataTableCell width={"12%"}>
                    <TableRowCell content={survey["name"]} clamp={1} padding="0"/>
                </DataTableCell>
                <DataTableCell textAlign="center">
                    {getSurveyTypeIcons(survey["surveyType"])}
                </DataTableCell>

                <DataTableCell>
                    {getSurveyUserGroupLabel(survey)}
                </DataTableCell>
                <DataTableCell>
                    {moment(survey["startDate"], "YYYY-MM-DD hh:mm:ss").format("D MMMM YYYY")}
                </DataTableCell>

                <DataTableCell>
                    {moment(survey["finishDate"], "YYYY-MM-DD hh:mm:ss").format("D MMMM YYYY")}
                </DataTableCell>
                <DataTableCell textAlign="center" padding="0.35rem 0.6rem">
                    { selectedSurveyOrPollStatusType === SurveyStatusType.COMPLETE || !canManageSurveys ?
                        "-"
                        :
                        <DataTableEditActionButton
                            onClick={() => editSurvey(survey)}
                            backgroundColor="var(--calendar-surface, #FFFFFF)"
                            color="var(--calendar-text, #171A22)"
                            border="1px solid var(--calendar-text, #171A22)"
                        >
                            Edit
                        </DataTableEditActionButton>
                    }
                </DataTableCell>
                <DataTableCell textAlign="center" padding="0.35rem 0.6rem">
                    {
                        survey["status"] !== "Scheduled"
                            ?
                            <DataTableViewActionButton
                                onClick={() => surveyInsights(survey.uuid)}
                                backgroundColor="var(--calendar-action-primary-bg, #5B6EF5)"
                                color="var(--calendar-action-primary-text, #FFFFFF)"
                                border="1px solid var(--calendar-action-primary-bg, #5B6EF5)"
                            >
                                View
                            </DataTableViewActionButton>
                            :
                            <span> - </span>
                    }

                </DataTableCell>
                <DataTableCell textAlign="center">
                    {
                        survey["status"] === "Scheduled"
                            ?
                            <div style={{ fontWeight: "500", color: "var(--calendar-text, #171A22)" }}>{survey["status"]}</div>
                            :
                            <div style={{ fontWeight: "500", color: "#77B500" }}>{survey["status"]}</div>
                    }
                </DataTableCell>

            </tr>
        );
    });

    return (
        <PortalPageShell>
            <SurveyAndPollsHeading
                handleChangeTableType={handleChangeTableType}
                selectedTableType={selectedTableType}
                user={user}
                featureContext={featureContext}
            />
            <UpperControls >
                {/* hide for now as mentioned by John at Sept 09, 2024 */}
                {/* <SurveyButton marginLeft="2.5rem" backgroundColor="#000000" onClick={() => { showHowItWorks(true); }}>
                    <span>How Does It Work?</span>
                </SurveyButton> */}
                <PortalTableControls>
                    <FilterControl>
                        <span>Filter:</span>
                        <InputSelector width="12.5rem" value={selectedSurveyOrPollStatusType} onChange={(e) => handleChangeStatusFilter(e.target.value)} >
                            <option value={SurveyStatusType.ACTIVE} style={{ fontSize: "1rem" }}>
                                Active
                            </option>
                            <option value={SurveyStatusType.COMPLETE} style={{ fontSize: "1rem" }}>
                                Complete
                            </option>
                            <option value={SurveyStatusType.SCHEDULED} style={{ fontSize: "1rem" }}>
                                Scheduled
                            </option>
                        </InputSelector>
                    </FilterControl>
                    {canManageSurveys && selectedSurveys.length > 0 && (
                        <SurveyButton
                            marginRight="0"
                            style={{ fontWeight: "700" }}
                            backgroundColor="#FF7171"
                            onClick={() => confirmDelete(selectedSurveys)}
                        >
                            <span>Delete Selected Surveys</span>
                        </SurveyButton>
                    )}
                </PortalTableControls>

                <PortalTableActions>
                    {canManageSurveys && (
                        <ActivateFeatureButton
                            width="190px"
                            onClick={() => showSurveyForm(true)}
                        >
                            <span>Create New Survey</span>
                        </ActivateFeatureButton>
                    )}
                </PortalTableActions>
            </UpperControls>

            { fetching &&
                <DataTableLoading minHeight="24rem" />
            }

            { !fetching &&
                <DataTableContainer maxWidth="98.625rem">
                    <DataTable rowHeight="3.05rem" cellPadding="0.45rem 0.85rem" headerPadding="0.7rem 0.85rem">
                        <thead>
                            <tr>
                                <DataTableHeaderCell width={"5%"}>
                                    <DataTableHeaderCheckboxContent>
                                        <label>
                                            <Checkbox readOnly checked={surveysArray.length === selectedSurveys.length} onChange={() => handleCheckbox()} key="0" />
                                        </label>
                                    </DataTableHeaderCheckboxContent>
                                </DataTableHeaderCell>
                                <DataTableHeaderCell width={"7%"} textAlign="center">
                                    <ColumnHeader heading="Image" />
                                </DataTableHeaderCell>

                                {/* column sorting does not show any icons at the moment, will add a fix to that later on */}
                                <DataTableHeaderCell width={"25%"} onClick={(e) => emitSortColumn("SURVEY_NAME")} >
                                    <ColumnHeader
                                        heading="Survey Name"
                                        sortDirection={sortableColumns[SortableColumns.SURVEY_NAME]}
                                        styleProps={{ justifyContent: "left" }}
                                    />
                                </DataTableHeaderCell>
                                <DataTableHeaderCell width={"10%"} onClick={() => emitSortColumn("SURVEY_TYPE")} >
                                    <ColumnHeader
                                        heading="Survey Type"
                                        sortDirection={sortableColumns[SortableColumns.SURVEY_TYPE]}
                                    />
                                </DataTableHeaderCell>
                                <DataTableHeaderCell width={"10%"} onClick={() => emitSortColumn("USER_GROUP")} >
                                    <ColumnHeader
                                        heading="User Group"
                                        sortDirection={sortableColumns[SortableColumns.USER_GROUP]}
                                        styleProps={{ justifyContent: "left" }}
                                    />
                                </DataTableHeaderCell>

                                <DataTableHeaderCell width={"10%"} onClick={() => emitSortColumn("START_DATE")} >
                                    <ColumnHeader
                                        heading="Start Date"
                                        sortDirection={sortableColumns[SortableColumns.START_DATE]}
                                        styleProps={{ justifyContent: "left" }}
                                    />
                                </DataTableHeaderCell>

                                <DataTableHeaderCell width={"10%"} onClick={() => emitSortColumn("FINISH_DATE")} >
                                    <ColumnHeader
                                        heading="Finish Date"
                                        sortDirection={sortableColumns[SortableColumns.FINISH_DATE]}
                                        styleProps={{ justifyContent: "left" }}
                                    />
                                </DataTableHeaderCell>

                                <DataTableHeaderCell width={"10%"} >
                                    <ColumnHeader
                                        heading="Edit"
                                    />
                                </DataTableHeaderCell>
                                <DataTableHeaderCell width={"10%"} >
                                    <ColumnHeader
                                        heading="Survey Insights"
                                    />
                                </DataTableHeaderCell>
                                <DataTableHeaderCell width={"10%"} onClick={() => emitSortColumn("STATUS")} >
                                    <ColumnHeader
                                        heading="Status"
                                        sortDirection={sortableColumns[SortableColumns.STATUS]}
                                    />
                                </DataTableHeaderCell>
                            </tr>
                        </thead>

                        <tbody>
                            {surveyList(surveysArray)}

                            {emptyList()}
                        </tbody>
                    </DataTable>
                </DataTableContainer>
            }
        </PortalPageShell>
    );
};


export default SurveyTable;
